import { useEffect, useState } from "react";
import { useParams, Link, useLocation } from "react-router-dom";
import { format } from "date-fns";
import { documentToReactComponents } from "@contentful/rich-text-react-renderer";
import { BLOCKS, INLINES, MARKS } from "@contentful/rich-text-types";
import LandingNav from "@/components/LandingNav";
import SEOHead from "@/components/SEOHead";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { fetchBlogPost, type BlogPost } from "@/lib/contentful";

const richTextOptions = {
  renderMark: {
    [MARKS.BOLD]: (text: React.ReactNode) => <strong className="font-semibold">{text}</strong>,
    [MARKS.ITALIC]: (text: React.ReactNode) => <em>{text}</em>,
  },
  renderNode: {
    [BLOCKS.HEADING_2]: (_node: any, children: React.ReactNode) => (
      <h2 className="font-serif text-2xl md:text-3xl text-foreground mt-10 mb-4">{children}</h2>
    ),
    [BLOCKS.HEADING_3]: (_node: any, children: React.ReactNode) => (
      <h3 className="font-serif text-xl md:text-2xl text-foreground mt-8 mb-3">{children}</h3>
    ),
    [BLOCKS.HEADING_4]: (_node: any, children: React.ReactNode) => (
      <h4 className="font-serif text-lg text-foreground mt-6 mb-2">{children}</h4>
    ),
    [BLOCKS.PARAGRAPH]: (_node: any, children: React.ReactNode) => (
      <p className="text-foreground/85 leading-relaxed mb-5">{children}</p>
    ),
    [BLOCKS.UL_LIST]: (_node: any, children: React.ReactNode) => (
      <ul className="list-disc pl-6 mb-5 space-y-2 text-foreground/85">{children}</ul>
    ),
    [BLOCKS.OL_LIST]: (_node: any, children: React.ReactNode) => (
      <ol className="list-decimal pl-6 mb-5 space-y-2 text-foreground/85">{children}</ol>
    ),
    [BLOCKS.LIST_ITEM]: (_node: any, children: React.ReactNode) => (
      <li className="leading-relaxed">{children}</li>
    ),
    [BLOCKS.QUOTE]: (_node: any, children: React.ReactNode) => (
      <blockquote className="border-l-4 border-primary/30 pl-5 my-6 italic text-muted-foreground">
        {children}
      </blockquote>
    ),
    [BLOCKS.EMBEDDED_ASSET]: (node: any) => {
      const url = node.data?.target?.fields?.file?.url;
      const title = node.data?.target?.fields?.title || "";
      if (!url) return null;
      return (
        <img
          src={`https:${url}`}
          alt={title}
          className="rounded-xl my-8 w-full"
          loading="lazy"
        />
      );
    },
    [INLINES.HYPERLINK]: (node: any, children: React.ReactNode) => (
      <a
        href={node.data.uri}
        target="_blank"
        rel="noopener noreferrer"
        className="text-primary underline underline-offset-4 hover:text-primary/80 transition-colors"
      >
        {children}
      </a>
    ),
  },
};

const BlogPostPage = () => {
  const { slug } = useParams<{ slug: string }>();
  const { search } = useLocation();
  const isPreview = new URLSearchParams(search).get("preview") === "true";
  const [post, setPost] = useState<BlogPost | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!slug) return;
    setLoading(true);
    setError(null);
    fetchBlogPost(slug, isPreview)
      .then((data) => {
        if (!data) {
          setError("Post not found");
        }
        setPost(data);
      })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [slug, isPreview]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <LandingNav />
        <main className="mx-auto max-w-3xl px-6 py-16 space-y-6">
          <Skeleton className="h-8 w-2/3" />
          <Skeleton className="h-4 w-1/3" />
          <Skeleton className="h-64 w-full rounded-xl" />
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-5/6" />
        </main>
      </div>
    );
  }

  if (error || !post) {
    return (
      <div className="min-h-screen bg-background">
        <LandingNav />
        <main className="mx-auto max-w-3xl px-6 py-16">
          <h1 className="font-serif text-3xl text-foreground mb-4">Post not found</h1>
          <p className="text-muted-foreground mb-6">
            {error || "We couldn't find that article."}
          </p>
          <Button asChild variant="outline">
            <Link to={`/blog${isPreview ? "?preview=true" : ""}`}>← Back to Blog</Link>
          </Button>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <SEOHead
        title={post.seoTitle || post.title}
        description={post.seoDescription || post.snippet || ""}
        path={`/blog/${post.slug}`}
      />
      <LandingNav />

      <main className="mx-auto max-w-3xl px-6 py-16">
        {/* Author + Date */}
        {(post.author || post.publishedDate) && (
          <div className="flex items-center gap-3 mb-8">
            {post.author && (
              <Avatar className="h-10 w-10">
                {post.authorPhoto ? (
                  <AvatarImage src={post.authorPhoto} alt={post.author} />
                ) : null}
                <AvatarFallback className="text-xs font-medium">
                  {post.author
                    .split(" ")
                    .map((n) => n[0])
                    .join("")
                    .slice(0, 2)
                    .toUpperCase()}
                </AvatarFallback>
              </Avatar>
            )}
            <div>
              {post.author && (
                <p className="text-sm font-medium text-foreground">{post.author}</p>
              )}
              {post.publishedDate && (
                <p className="text-xs text-muted-foreground">
                  {format(new Date(post.publishedDate), "MMMM d, yyyy")}
                </p>
              )}
            </div>
          </div>
        )}

        {/* Title */}
        <h1 className="font-serif text-3xl md:text-4xl lg:text-5xl text-foreground mb-8 leading-tight">
          {post.title}
        </h1>

        {/* Hero image */}
        {post.featuredImage && (
          <img
            src={post.featuredImage}
            alt={post.title}
            className="w-full rounded-xl mb-10 aspect-[16/9] object-cover"
          />
        )}

        {/* Rich text body */}
        <article className="prose-custom">
          {post.body ? documentToReactComponents(post.body, richTextOptions) : null}
        </article>

        {/* Back link */}
        <div className="mt-12 pt-8 border-t border-border">
          <Link
            to={`/blog${isPreview ? "?preview=true" : ""}`}
            className="text-sm text-muted-foreground hover:text-foreground transition-colors"
          >
            ← All articles
          </Link>
        </div>
      </main>

      {/* Sticky CTA */}
      <div className="sticky bottom-0 border-t border-border bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/80">
        <div className="mx-auto max-w-3xl px-6 py-5 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-sm text-muted-foreground text-center sm:text-left">
            Stop the quiet guilt. Start building your Relationship Memory today.
          </p>
          <Button asChild className="rounded-full px-6 text-sm font-medium shrink-0">
            <Link to="/signup?utm_source=blog&utm_medium=content&utm_campaign=relationship_memory_launch">Get Started →</Link>
          </Button>
        </div>
      </div>
    </div>
  );
};

export default BlogPostPage;
